import { generatePath, Outlet, useNavigate, useParams } from "react-router-dom";
import { Formik, Form } from "formik";
import useCheckoutSteps, { ICheckout } from "@/pages/checkout/hooks/useCheckoutSteps";
import useSendOrder from "@/pages/checkout/hooks/useSendOrder";
import { AppBar, IconButton, Stack, Toolbar, Typography } from "@mui/material";
import { ArrowLeft, ArrowRight } from "lucide-react";
import LoadingButton from "@mui/lab/LoadingButton";
import routes from "@/constants/routes";
import { useContext, useEffect, useMemo } from "react";
import useUserProfile from "@/hooks/useUserProfile";
import { Organization, User } from "@/types";
import { WebsiteContext } from "@/pages/website/WebsiteProvider";

export type CheckoutSharedProps = {
  activeUser: User | null;
  organization?: Organization | null;
};

type Params = {
  organizationAlias: string | undefined;
  orderId: string | undefined;
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const sendOrder = useSendOrder();
  const { activeUser } = useUserProfile();
  const { organization } = useContext(WebsiteContext);
  const { organizationAlias, orderId } = useParams<Params>();
  const { stepId, steps, previousStep, nextStep, isFirstStep, isLastStep, initialValues, validationSchema, activeStep } = useCheckoutSteps();

  const sharedProps: CheckoutSharedProps = useMemo(() => {
    return { activeUser, organization };
  }, [activeUser, organization]);

  const onSubmit = (values: ICheckout) => {
    if (isLastStep) {
      sendOrder(values);
    }

    // execute step tasks
    if (activeStep?.onSubmit) {
      activeStep.onSubmit(values, sharedProps);
    }

    // continue
    navigate(
      generatePath(routes.organizations.order.checkoutForm, {
        organizationAlias,
        orderId,
        formId: nextStep.id,
      }) + location.search
    );
  };

  const onBack = () => {
    if (isFirstStep) {
      navigate(
        generatePath(routes.organizations.menu.main, {
          organizationAlias,
        }) + location.search
      );
    } else {
      navigate(
        generatePath(routes.organizations.order.checkoutForm, {
          organizationAlias,
          orderId,
          formId: previousStep.id,
        }) + location.search
      );
    }
  };

  useEffect(() => {
    if (stepId === "checkout") {
      navigate(
        generatePath(routes.organizations.order.checkoutForm, {
          organizationAlias,
          orderId,
          formId: steps[0].id,
        }) + location.search
      );
    }
  }, [navigate, orderId, organizationAlias, stepId, steps]);

  if (!activeStep) {
    return <p style={{ color: "white" }}>Loading data...</p>;
  }

  return (
    <Stack
      sx={{
        height: "100dvh",
        backgroundColor: "#DDEAFD",
        "& form": {
          height: "100%",
          display: "flex",
        },
      }}
    >
      <AppBar position="static" color="default" elevation={2}>
        <Toolbar sx={{ py: 1.5 }}>
          <IconButton size="large" edge="start" color="inherit" aria-label="menu" sx={{ ml: -0.5, mr: 1 }} onClick={onBack}>
            <ArrowLeft />
          </IconButton>
          <Typography variant="h3">{activeStep.title}</Typography>
        </Toolbar>
      </AppBar>
      <Formik onSubmit={onSubmit} initialValues={initialValues} validationSchema={validationSchema} enableReinitialize={true}>
        {() => {
          return (
            <Form>
              <Stack flex={1} padding={3} spacing={2} justifyContent="space-between">
                <Outlet context={{ steps }} />
                <LoadingButton size="large" type="submit" endIcon={!isLastStep && <ArrowRight />} variant="contained" fullWidth color={isLastStep ? "success" : "primary"}>
                  {isLastStep ? "Finalizar" : "Continuar"}
                </LoadingButton>
              </Stack>
            </Form>
          );
        }}
      </Formik>
    </Stack>
  );
};

export default CheckoutPage;
